import { api } from './api';

export const productApi = api.injectEndpoints({
  tagTypes: ['Product', 'Category', 'Brand'],
  endpoints: (builder) => ({
    // Get all products for a brand
    getProducts: builder.query({
      query: ({ id, page = 1, limit = 10, status, search, category }) => ({
        url: `/products/brand/${id}`,
        params: { page, limit, status, search, category },
      }),
      providesTags: (result) => {
        if (!result || !result.data) return [{ type: 'Product', id: 'LIST' }];

        return [
          ...result.data.map((product) => ({ type: 'Product', id: product._id })),
          { type: 'Product', id: 'LIST' },
        ];
      },
    }),

    // Get product by ID
    getProductById: builder.query({
      query: (id) => `/products/${id}`,
      providesTags: (result, error, id) => [{ type: 'Product', id }],
    }),

    // Create product
    createProduct: builder.mutation({
      query: (productData) => ({
        url: '/products',
        method: 'POST',
        body: productData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    updateProduct: builder.mutation({
      query: ({ id, body }) => ({
        url: `/products/${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Product', id }],
    }),

    // Delete product (soft delete)
    deleteProduct: builder.mutation({
      query: ({ id, permanent = false }) => ({
        url: `/products/${id}`,
        method: 'DELETE',
        params: { permanent },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        { type: 'Product', id: 'LIST' },
      ],
    }),

    // Search products
    searchProducts: builder.query({
      query: ({ search, page = 1, limit = 10, status, category }) => ({
        url: '/products/search',
        params: { search, page, limit, status, category },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: 'Product', id: _id })),
              { type: 'Product', id: 'LIST' },
            ]
          : [{ type: 'Product', id: 'LIST' }],
    }),

    // Get all categories
    getAllCategories: builder.query({
      query: (tree = false) => ({
        url: '/categories',
        params: { tree: tree ? 'true' : undefined },
      }),
      providesTags: ['Category'],
    }),

    // Get seller brands
    getSellerBrands: builder.query({
      query: (sellerId) => `/sellers/${sellerId}/brands`,
      providesTags: (result) => {
        if (!result || !result.data) return [{ type: 'Brand', id: 'LIST' }];

        return [
          ...result.data.filter(Boolean).map(({ _id }) => ({ type: 'Brand', id: _id })),
          { type: 'Brand', id: 'LIST' },
        ];
      },
    }),

    // Get all brands
    getAllBrands: builder.query({
      query: () => '/brands',
      providesTags: ['Brand'],
    }),

    // Upload product images
    uploadProductImages: builder.mutation({
      query: ({ productId, formData }) => ({
        url: `/products/${productId}/images`,
        method: 'POST',
        body: formData,
        headers: {
          // Let the browser set Content-Type for FormData
        },
      }),
      invalidatesTags: (result, error, { productId }) => [{ type: 'Product', id: productId }],
    }),
    // Get Seller Brand Products
    getSellerBrandProducts: builder.query({
      query: ({ sellerId, page = 1, limit = 10, search, status, category, brand }) => ({
        url: `/products/seller/${sellerId}`,
        params: {
          page,
          limit,
          ...(search && { search }),
          ...(status && { status }),
          ...(category && { category }),
          ...(brand && { brand }),
        },
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: 'Product', id: _id })),
              { type: 'Product', id: 'LIST' },
            ]
          : [{ type: 'Product', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetProductsQuery,
  useGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useSearchProductsQuery,
  useGetAllCategoriesQuery,
  useUploadProductImagesMutation,
  useLazyGetProductsQuery,
  useLazyGetProductByIdQuery,
  useLazySearchProductsQuery,
  useLazyGetAllCategoriesQuery,
  useGetAllBrandsQuery,
  useGetSellerBrandsQuery,
  useGetSellerBrandProductsQuery,
} = productApi;
